// Collection-only projections. Retained Plan readers keep their own contracts.
import { evaluateMonth, monthKey, STATUS_RANK, STATUS_LABEL, disclosureLines, DEFAULT_TARGET_BUFFER_PCT } from "../capacity/capacityModel.js";
import { recordDateDay, selectRecordDateEvents } from "../leases/recordDateEvents.js";

const MEASURED = new Set(["under", "comfortable", "tight", "over"]);
const finite = n => typeof n === "number" && Number.isFinite(n);
const officeId = office => String(office.locationId ?? office.id ?? "");
const CAPACITY_SORT_KEYS = new Set(["capacity", "people", "seatsCapacity"]);

// Policy affects only the ladder's verdict; People/installed depend on the
// snapshot alone. Failed reads settle too: unknowns must not block forever.
export function marketsListSortPending(primary, secondary, snapshotStatus, policyPending, inventoryStatus = snapshotStatus) {
  const keys = [primary, secondary];
  return keys.some(key => CAPACITY_SORT_KEYS.has(key) && ["idle", "loading"].includes(key === "capacity" ? snapshotStatus : inventoryStatus))
    || (keys.includes("seats") && ["idle", "loading"].includes(inventoryStatus))
    || (keys.includes("capacity") && policyPending);
}

export function marketCountryIdentity(locations = []) {
  // Country must belong to the selected office, even when it is blank.
  const offices = [...locations].sort((a, b) => (
    Number(Boolean(b.isHub)) - Number(Boolean(a.isHub)) ||
    (!a.isHub && !b.isHub
      ? (finite(b.installedCapacity) ? b.installedCapacity : -Infinity) - (finite(a.installedCapacity) ? a.installedCapacity : -Infinity)
      : 0) ||
    officeId(a).localeCompare(officeId(b))
  ));
  const primary = offices[0] ?? null;
  const countries = [...new Set(locations.map(l => l.country).filter(c => typeof c === "string" && c.trim()))].sort();
  return { primaryOfficeId: primary ? officeId(primary) : null, country: primary?.country?.trim() ? primary.country : null, countries };
}

export function currentMarketLadders(snapshot, referenceDay = new Date(), bufferPct = DEFAULT_TARGET_BUFFER_PCT) {
  const key = recordDateDay(referenceDay)?.slice(0, 7);
  if (!snapshot || ["idle", "loading"].includes(snapshot.status)) return { status: "loading", month: key, byId: new Map() };
  if (snapshot.status === "error") return { status: "error", error: snapshot.error, month: key, byId: new Map() };
  const { months, markets } = snapshot.data ?? {};
  if (!Array.isArray(months) || !Array.isArray(markets)) return { status: "error", error: "Capacity response was incomplete.", month: key, byId: new Map() };
  const index = months.findIndex(m => monthKey(m.start) === key);
  if (index < 0) return { status: "error", error: `Capacity has no ${key} baseline.`, month: key, byId: new Map() };
  const byId = new Map(markets.map(m => {
    // Server month arrays can be indexed or keyed; never use a scenario/focus.
    const input = m.months?.some(month => month?.start)
      ? m.months.find(month => monthKey(month.start) === key) : m.months?.[index];
    const cell = evaluateMonth(input, index, null, bufferPct);
    return [m.id, { status: "ok", month: key, cell, measured: MEASURED.has(cell.status),
      label: STATUS_LABEL[cell.status], disclosure: disclosureLines(cell).join(" ") }];
  }));
  return { status: "ok", month: key, byId };
}

export function ladderForMarket(ladders, id) {
  return ladders.status === "ok" ? ladders.byId.get(id) ?? { status: "excluded", month: ladders.month }
    : { status: ladders.status, month: ladders.month, error: ladders.error };
}

export function selectMarketNextDate(market, referenceDay = new Date()) {
  const offices = market.locations;
  if (!Array.isArray(offices)) {
    return { status: "unavailable", next: null, ambiguities: [] };
  }
  const candidates = [];
  const ambiguities = [];
  let complete = true;
  for (const office of offices) {
    if (!Array.isArray(office.recordDateLeases)) { complete = false; continue; }
    for (const lease of office.recordDateLeases) {
      if (!Array.isArray(lease.criticalDates)) { complete = false; continue; }
      const result = selectRecordDateEvents(lease, lease.criticalDates, referenceDay);
      ambiguities.push(...result.ambiguities);
      if (result.next) candidates.push({ ...result.next, locationId: officeId(office) });
    }
  }
  candidates.sort(compareDateEvents);
  return { status: complete ? "ok" : "unavailable", next: candidates[0] ?? null, ambiguities };
}

export function compareDateEvents(a, b) {
  return a.date.localeCompare(b.date) || String(a.leaseId).localeCompare(String(b.leaseId)) ||
    String(a.id).localeCompare(String(b.id));
}

export function marketEventLabel(event) {
  return /notice/.test(event?.type) ? "Notice" : event?.type === "expiration" ? "Expiry" : "Decision";
}

export function marketDateDisclosure(selection) {
  const event = selection?.next;
  if (!selection || (selection.status !== "ok" && !event)) return "Lease dates unavailable: complete lease/date evidence has not loaded.";
  const ambiguity = `${selection.status !== "ok" ? " Incomplete lease/date evidence; this known candidate may not be the earliest." : ""}${selection.ambiguities.length ? " Additional date evidence needs source review." : ""}`;
  if (!event) return selection.ambiguities.length ? "Date evidence needs source review; absence is not confirmed." : "No upcoming lease dates.";
  return `${event.date}: ${event.label} (${event.leaseId}, ${event.locationId}). ${event.sourceRow?.description || ""} ${event.provenance === "calculated" ? "Calculated: " : event.provenance === "ambiguous" ? "Source unverified: " : "Recorded. "}${event.disclosure || ""}${ambiguity}`;
}

export function summarizeMarketsList(markets) {
  const unavailable = markets.filter(m => !m.capacityLadder?.measured
    && m.capacityLadder?.status !== "excluded"
    && !["excluded", "no_seats"].includes(m.capacityLadder?.cell?.status));
  const over = markets.filter(m => m.capacityLadder?.measured && m.capacityLadder.cell.status === "over").length;
  const dated = markets.map(m => m.nextRecordDate?.next).filter(Boolean).sort(compareDateEvents);
  const next = dated[0] ?? null;
  const incompleteDates = markets.some(m => m.nextRecordDate?.status !== "ok" || m.nextRecordDate.ambiguities.length);
  const date = next ? new Date(`${next.date}T00:00:00`) : null;
  return { over, unavailable, confirmedCapacity: unavailable.length === 0,
    next, incompleteDates, quarter: date ? `Q${Math.floor(date.getMonth() / 3) + 1} ${date.getFullYear()}` : null };
}

export function marketsListSortValue(key, market) {
  if (key === "capacity") return market.capacityLadder?.measured ? STATUS_RANK[market.capacityLadder.cell.status] : null;
  if (key === "nextDate") return market.nextRecordDate?.next?.date ?? null;
  if (key === "cost") return finite(market.totalAnnualCost) ? market.totalAnnualCost : null;
  return null;
}
